/** The browser's end of the `prices:{symbol}` channel (SM-P6-04).
 *
 *  One WebSocket for the whole app, opened lazily on the first subscription and only under the http binding —
 *  under the sim there is no fabric and deliberately no fake one, so a missing provider stays a silent channel
 *  rather than a plausible feed. The socket is ticketed exactly like every other fabric consumer: a 60-second,
 *  single-use, device-bound ticket from `engine.realtime.ticket`, consumed at upgrade. The socket outlives its
 *  ticket (the ticket authorises the HANDSHAKE); a drop re-tickets and re-subscribes with backoff.
 *
 *  Symbols ride the ticket request (the chart's instrument need not be on the watchlist), so subscribing to a new
 *  symbol after the socket is up may require a fresh ticket — the implementation simply reconnects when asked for
 *  a symbol the current connection was not authorised for. Coarse, and correct: retargeting instruments is a
 *  human-speed event.
 */

interface PriceTick {
  readonly symbol: string;
  readonly last: number;
  /** Milliseconds since epoch — parsed from the frame's ISO stamp, which carries the provider's own clock. */
  readonly at: number;
  readonly provenance: string;
}

type TickHandler = (tick: PriceTick) => void;

const API_BINDING = (import.meta.env['VITE_API_BINDING'] as string | undefined) ?? 'sim';
const API_ORIGIN =
  API_BINDING === 'http'
    ? ((import.meta.env['VITE_API_ORIGIN'] as string | undefined) ?? 'http://localhost:3000')
    : '';

const handlers = new Map<string, Set<TickHandler>>();
let socket: WebSocket | null = null;
let authorised = new Set<string>();
let connecting = false;
let backoffMs = 1_000;
let closedByUs = false;

function wsUrl(ticket: string, deviceId: string): string {
  const base = API_ORIGIN || window.location.origin;
  return `${base.replace(/^http/, 'ws')}/ws?ticket=${encodeURIComponent(ticket)}&deviceId=${encodeURIComponent(deviceId)}`;
}

async function fetchTicket(symbols: readonly string[]): Promise<{ ticket: string; deviceId: string } | null> {
  try {
    const session = await fetch(`${API_ORIGIN}/api/trpc/auth.session.current?batch=1`, {
      credentials: 'include',
    }).then((r) => r.json() as Promise<[{ result?: { data?: { deviceId?: string } | null } }]>);
    const deviceId = session?.[0]?.result?.data?.deviceId;
    if (!deviceId) return null;

    const issued = await fetch(`${API_ORIGIN}/api/trpc/engine.realtime.ticket?batch=1`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ 0: { deviceId, channels: ['prices'], symbols: [...symbols] } }),
    }).then((r) => r.json() as Promise<[{ result?: { data?: { ticket?: string } } }]>);
    const ticket = issued?.[0]?.result?.data?.ticket;
    return ticket ? { ticket, deviceId } : null;
  } catch {
    return null;
  }
}

async function connect(): Promise<void> {
  if (connecting || API_BINDING !== 'http') return;
  connecting = true;
  try {
    const wanted = [...handlers.keys()];
    if (wanted.length === 0) return;
    const granted = await fetchTicket(wanted);
    if (!granted) return; // signed out, or the ticket refused — the chart simply does not tick, and says nothing false.

    closedByUs = false;
    const ws = new WebSocket(wsUrl(granted.ticket, granted.deviceId));
    socket = ws;
    authorised = new Set(wanted);

    ws.onopen = () => {
      backoffMs = 1_000;
      for (const symbol of authorised)
        ws.send(JSON.stringify({ type: 'subscribe', channel: `prices:${symbol}` }));
    };
    ws.onmessage = (event) => {
      let frame: { channel?: string; payload?: Record<string, unknown> };
      try {
        frame = JSON.parse(String(event.data)) as typeof frame;
      } catch {
        return;
      }
      const channel = frame.channel ?? '';
      if (!channel.startsWith('prices:') || !frame.payload) return;
      const symbol = channel.slice('prices:'.length);
      const last = frame.payload['last'];
      const at = Date.parse(String(frame.payload['at'] ?? ''));
      if (typeof last !== 'number' || !Number.isFinite(at)) return;
      const tick: PriceTick = {
        symbol,
        last,
        at,
        provenance: String(frame.payload['provenance'] ?? 'unknown'),
      };
      for (const handler of handlers.get(symbol) ?? []) handler(tick);
    };
    ws.onclose = () => {
      socket = null;
      if (closedByUs || handlers.size === 0) return;
      const delay = backoffMs;
      backoffMs = Math.min(30_000, backoffMs * 2);
      setTimeout(() => void connect(), delay);
    };
  } finally {
    connecting = false;
  }
}

/** Subscribe to a symbol's ticks. Returns an unsubscribe. Under the sim binding this is a deliberate no-op. */
export function onPrice(symbol: string, handler: TickHandler): () => void {
  if (API_BINDING !== 'http') return () => undefined;
  const key = symbol.toUpperCase();
  const set = handlers.get(key) ?? new Set();
  set.add(handler);
  handlers.set(key, set);

  if (socket && authorised.has(key) && socket.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify({ type: 'subscribe', channel: `prices:${key}` }));
  } else if (!socket || !authorised.has(key)) {
    // A symbol the current ticket did not authorise needs a fresh handshake. Retargeting is human-speed; a
    // reconnect is cheaper than being subtle about it.
    closedByUs = true;
    socket?.close();
    socket = null;
    void connect();
  }

  return () => {
    const current = handlers.get(key);
    current?.delete(handler);
    if (current && current.size === 0) handlers.delete(key);
    if (handlers.size === 0) {
      closedByUs = true;
      socket?.close();
      socket = null;
    }
  };
}

export type { PriceTick };
